import { useCallback } from "react";

import { Product } from "@/api/product-api";
import { useCurrency } from "@/hooks/useCurrency";
import { useLocale } from "@/store/locale-store";
import { isPricedForListing } from "@/libs/product/priced-for-listing";

/**
 * ★판매 목록용 필터 — **이 국가·통화에서 가격이 있는 제품만** 남긴다.
 *
 * 가격 판정은 `ProductUtil.getProductPrice`(정가 0·미등록 = 가격 없음, #5886) 그대로다. 가격 없는 제품은
 * 장바구니·결제에서 이미 막히지만(#5886) 목록에는 가격 칸이 빈 카드로 남았다. 이 필터를 **목록이 처음
 * 만들어지는 자리**(데이터 원천)에 건다 — 카드에 걸면 같은 카드를 쓰는 PDP 구매 영역까지 사라진다.
 *
 * - 단종 제품은 남긴다 — 가격 칸에 「단종」 표시가 나가는 기존 동작(`isDiscontinued`). PDP 리다이렉트
 *   (`useRedirectWhenUnpriced`)와 같은 예외다.
 * - 제품 **데이터 원천(`useProducts`)에는 걸지 않는다** — 번들 구성품 표시(`getBundleComponents`)가 SKU 로
 *   제품을 찾으므로, 거기서 빼면 「번들에 든 배지」가 구성품에서도 사라진다.
 * - 국가는 SSR·첫 렌더에서 `""`, 통화는 GE flag 로딩 중 USD 다 → 그동안은 기준가로 판정되고 확정 뒤
 *   다시 거른다(「잠깐 보였다 사라짐」, VR 게임 목록과 같은 트레이드오프).
 */
export function usePricedFilter() {
  const currency = useCurrency();
  const { countryCode } = useLocale();

  return useCallback(
    <T extends Product>(products: T[] | undefined | null): T[] =>
      (products ?? []).filter((product) =>
        isPricedForListing(product, { currency, countryCode }),
      ),
    [currency, countryCode],
  );
}
